import { useState, useEffect } from "react";
import { api, formatApiError } from "@/lib/api";
import { useT } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { toast } from "sonner";
import { Users, Trophy, Activity, Link2, Pencil, Trash2, Search, X, Shield, UserCog, Eye, EyeOff, Power, KeyRound } from "lucide-react";

function StatCard({ label, value, icon: Icon, accent = "#05070A", testid }) {
  return (
    <div className="ct-card p-5" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{label}</div>
        <Icon size={16} style={{ color: accent }} />
      </div>
      <div className="stat-num text-4xl mt-1 tabular" style={{ color: accent }}>{value}</div>
    </div>
  );
}

function EditModal({ user, onClose, onSaved }) {
  const [form, setForm] = useState({ name: user.name || "", email: user.email || "", phone: user.phone || "", role: user.role || "player" });
  const [newPassword, setNewPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...form };
      if (newPassword) payload.password = newPassword;
      const { data } = await api.put(`/admin/users/${user.id}`, payload);
      toast.success("User updated"); onSaved({ ...data, password_plain: newPassword || data.password_plain });
    }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="edit-user-modal">
        <div className="flex items-center justify-between mb-4"><h2 className="font-head font-bold text-2xl">Edit user</h2><button onClick={onClose}><X /></button></div>
        <div className="space-y-3">
          <div><Label className="text-xs">Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" data-testid="edit-name" /></div>
          <div><Label className="text-xs">Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" data-testid="edit-email" /></div>
          <div><Label className="text-xs">Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1" data-testid="edit-phone" /></div>
          <div><Label className="text-xs">Role</Label>
            <select className="w-full h-9 rounded-md border border-slate-200 px-2 text-sm mt-1" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="edit-role">
              {["player", "coach", "admin"].map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs flex items-center gap-1"><KeyRound size={12} /> Current password</Label>
            <div className="text-sm font-mono bg-slate-100 rounded px-2 py-1 mt-1">{user.password_plain || "— (set below to reset)"}</div>
          </div>
          <div><Label className="text-xs">Reset password (optional)</Label><Input value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Leave blank to keep current" className="mt-1" data-testid="edit-password" /></div>
        </div>
        <div className="flex gap-2 mt-5"><Button variant="ghost" className="flex-1" onClick={onClose}>Cancel</Button><Button className="flex-1 bg-brand hover:bg-brand-dark" onClick={save} disabled={busy} data-testid="save-user-btn">{busy ? "Saving…" : "Save"}</Button></div>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const t = useT();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [role, setRole] = useState("");
  const [editing, setEditing] = useState(null);
  const [revealed, setRevealed] = useState({});

  const loadStats = async () => { const { data } = await api.get("/admin/stats"); setStats(data); };
  const loadUsers = async () => { const qs = new URLSearchParams({ ...(q && { q }), ...(role && { role }) }).toString(); const { data } = await api.get(`/admin/users?${qs}`); setUsers(data); };

  useEffect(() => { loadStats(); }, []);
  useEffect(() => { const t = setTimeout(loadUsers, 300); return () => clearTimeout(t); }, [q, role]);

  const del = async (u) => {
    if (!window.confirm(`Delete ${u.name} and all their data? This cannot be undone.`)) return;
    try { await api.delete(`/admin/users/${u.id}`); toast.success("User deleted"); loadUsers(); loadStats(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggle = async (u) => {
    try { const { data } = await api.put(`/admin/users/${u.id}/toggle`); setUsers((prev) => prev.map((x) => x.id === u.id ? { ...x, disabled: data.disabled } : x)); toast.success(data.disabled ? "User disabled" : "User enabled"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const roleBadge = (r) => r === "admin" ? "bg-brand/10 text-brand" : r === "coach" ? "bg-blue-100 text-blue-700" : "bg-slate-100 text-slate-600";

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2"><Shield className="text-brand" /><h1 className="font-head font-bold text-3xl">{t("Admin Console")}</h1></div>

      {!stats ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">{[1, 2, 3, 4].map((i) => <div key={i} className="skeleton h-28" />)}</div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard label="Total Users" value={stats.total_users} icon={Users} testid="admin-stat-users" />
            <StatCard label="Players" value={stats.players} icon={Trophy} accent="#DC2626" testid="admin-stat-players" />
            <StatCard label="Coaches" value={stats.coaches} icon={UserCog} accent="#1E40AF" testid="admin-stat-coaches" />
            <StatCard label="Entries Logged" value={stats.total_entries} icon={Activity} accent="#D97706" testid="admin-stat-entries" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard label="Active Coach Links" value={stats.active_links} icon={Link2} testid="admin-stat-links" />
            <StatCard label="Goals Set" value={stats.total_goals} icon={Trophy} testid="admin-stat-goals" />
            <StatCard label="Coach Notes" value={stats.total_notes} icon={Activity} testid="admin-stat-notes" />
            <StatCard label="Onboarded Players" value={`${stats.onboarded_players}/${stats.players}`} icon={Users} testid="admin-stat-onboarded" />
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            <div className="ct-card p-5 md:col-span-2" data-testid="signups-chart">
              <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">Signups · last 14 days</div>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={stats.signups}><CartesianGrid strokeDasharray="3 3" stroke="#eee" /><XAxis dataKey="date" tick={{ fontSize: 10 }} /><YAxis allowDecimals={false} tick={{ fontSize: 10 }} /><Tooltip /><Bar dataKey="count" fill="#D90416" radius={[4, 4, 0, 0]} /></BarChart>
              </ResponsiveContainer>
            </div>
            <div className="ct-card p-5" data-testid="top-active">
              <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">Most active players</div>
              <div className="space-y-2">
                {stats.top_active.length === 0 ? <p className="text-slate-400 text-sm">No activity yet.</p> : stats.top_active.map((p, i) => (
                  <div key={i} className="flex items-center justify-between text-sm"><span className="font-semibold truncate">{i + 1}. {p.name}</span><span className="font-mono text-slate-500">{p.entries}</span></div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}

      {/* users table */}
      <div className="ct-card p-5" data-testid="admin-users">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h2 className="font-head font-bold text-xl">Users</h2>
          <div className="flex gap-2">
            <div className="relative"><Search className="absolute left-3 top-2.5 text-slate-400" size={16} /><Input className="pl-9 h-9 w-56" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or email" data-testid="admin-user-search" /></div>
            <select className="h-9 rounded-md border border-slate-200 px-2 text-sm" value={role} onChange={(e) => setRole(e.target.value)} data-testid="admin-role-filter">
              {[["", "All roles"], ["player", "Players"], ["coach", "Coaches"], ["admin", "Admins"]].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[10px] uppercase tracking-widest text-slate-400 border-b"><th className="py-2 pr-3">Name</th><th className="py-2 pr-3">Email</th><th className="py-2 pr-3">Role</th><th className="py-2 pr-3">Password</th><th className="py-2 pr-3">Status</th><th className="py-2 pr-3">Entries</th><th className="py-2 text-right">Actions</th></tr></thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-b last:border-0 hover:bg-slate-50" data-testid={`user-row-${u.id}`}>
                  <td className="py-2.5 pr-3 font-semibold">{u.name}</td>
                  <td className="py-2.5 pr-3 text-slate-500">{u.email}</td>
                  <td className="py-2.5 pr-3"><span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${roleBadge(u.role)}`}>{u.role}</span></td>
                  <td className="py-2.5 pr-3">
                    <div className="flex items-center gap-1.5">
                      <span className="font-mono text-xs" data-testid={`pwd-${u.id}`}>{u.password_plain ? (revealed[u.id] ? u.password_plain : "••••••") : "—"}</span>
                      {u.password_plain && <button onClick={() => setRevealed((r) => ({ ...r, [u.id]: !r[u.id] }))} className="text-slate-400 hover:text-slate-900" data-testid={`reveal-pwd-${u.id}`}>{revealed[u.id] ? <EyeOff size={13} /> : <Eye size={13} />}</button>}
                    </div>
                  </td>
                  <td className="py-2.5 pr-3">
                    <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${u.disabled ? "bg-red-100 text-brand" : "bg-green-100 text-green-700"}`} data-testid={`status-${u.id}`}>{u.disabled ? "Disabled" : "Active"}</span>
                  </td>
                  <td className="py-2.5 pr-3 font-mono">{u.role === "player" ? (u.entry_count ?? 0) : "—"}</td>
                  <td className="py-2.5 text-right whitespace-nowrap">
                    {u.role !== "admin" && <button onClick={() => toggle(u)} className={`p-1.5 ${u.disabled ? "text-green-600" : "text-slate-400 hover:text-amber-600"}`} title={u.disabled ? "Enable" : "Disable"} data-testid={`toggle-user-${u.id}`}><Power size={15} /></button>}
                    <button onClick={() => setEditing(u)} className="p-1.5 text-slate-400 hover:text-slate-900" data-testid={`edit-user-${u.id}`}><Pencil size={15} /></button>
                    {u.role !== "admin" && <button onClick={() => del(u)} className="p-1.5 text-slate-400 hover:text-brand" data-testid={`delete-user-${u.id}`}><Trash2 size={15} /></button>}
                  </td>
                </tr>
              ))}
              {users.length === 0 && <tr><td colSpan={7} className="py-6 text-center text-slate-400">No users found.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {editing && <EditModal user={editing} onClose={() => setEditing(null)} onSaved={(u) => { setEditing(null); setUsers((prev) => prev.map((x) => x.id === u.id ? { ...x, ...u } : x)); loadStats(); }} />}
    </div>
  );
}
